import { Component, OnDestroy, OnInit } from '@angular/core';
import { SisifoCenter } from '@app/core/users';
import { CenterService } from '@app/features/patient-requests/services/center.service';
import { DialogService } from 'primeng/dynamicdialog';
import { Subject, takeWhile } from 'rxjs';
import { CenterDialogComponent } from '../center-dialog/center-dialog.component';
import { TranslateService } from '@ngx-translate/core';
import { YesNoDialogComponent } from '@app/shared';
import { ToastrService } from 'ngx-toastr';

export const CENTER_DIALOG_SIZE = {
  height: '98vh',
  width: '800px',
  style: { maxHeight: '98vh' },
};

@Component({
  selector: 'afoone-center-list',
  templateUrl: './center-list.component.html',
})
export class CenterListComponent implements OnInit, OnDestroy {
  public records$: Subject<SisifoCenter[]>;
  public selectedRecords: SisifoCenter[];

  private destroyed: boolean;

  constructor(
    private service: CenterService,
    private dialogService: DialogService,
    private _translateService: TranslateService,
    private _toastr: ToastrService
  ) {
    this.records$ = new Subject();
  }

  public ngOnInit(): void {
    this.loadRecords();
  }

  private async loadRecords(): Promise<void> {
    this.selectedRecords = [];

    try {
      const records = await this.service.getCenters();
      this.records$.next(records);
    } catch (error) {
      console.error('Error loading centers:', error);
    }
  }

  public onRowEdit(center: SisifoCenter): void {
    this.dialogService
      .open(CenterDialogComponent, {
        data: {
          center,
        },
        ...CENTER_DIALOG_SIZE,
        header: this._translateService.instant('WEB_RESULTS.CENTERS.EDIT'),
      })
      .onClose.pipe(takeWhile(() => !this.destroyed))
      .subscribe((record) => {
        if (record) {
          this.loadRecords();
        }
      });
  }

  public deleteSelected(): void {
    if (!this.selectedRecords || this.selectedRecords.length === 0) {
      return;
    }
    this.dialogService
      .open(YesNoDialogComponent, {
        height: '300px',
        width: '300px',
        header: this._translateService.instant('WEB_RESULTS.CENTERS.DELETE'),
        data: {
          message: this._translateService.instant(
            'WEB_RESULTS.CENTERS.DELETE_CONFIRMATION',
            {
              count: this.selectedRecords.length,
            }
          ),
        },
      })
      .onClose.pipe(takeWhile(() => !this.destroyed))
      .subscribe((result) => {
        if (result) {
          this.service
            .deleteMultiple(this.selectedRecords.map((r) => r.centroId))
            .then(() => {
              this.loadRecords();
            })
            .catch((err) => {
              const status = err?.status ?? err?.error?.status
              const message = err?.error?.message ?? err?.message
              const isRelatedUsers =
                status === 409 || message === 'CENTRE_DELETE_RELATED_USERS'
              const msg = isRelatedUsers
                ? this._translateService.instant(
                    'WEB_RESULTS.CENTERS.CENTRE_DELETE_RELATED_USERS'
                  )
                : message || this._translateService.instant('WEB_RESULTS.ERROR.DB_ERROR_SEARCHING')
              this._toastr.error(msg)
            })
        }
      });
  }

  public newRecord(): void {
    this.dialogService
      .open(CenterDialogComponent, {
        ...CENTER_DIALOG_SIZE,
        header: this._translateService.instant('WEB_RESULTS.CENTERS.NEW'),
      })
      .onClose.pipe(takeWhile(() => !this.destroyed))
      .subscribe((record) => {
        if (record) {
          this.loadRecords();
        }
      });
  }

  public ngOnDestroy(): void {
    this.destroyed = true;
  }
}
